import type { Properties } from 'posthog-js';
import type { UserIdentification } from './ExchangeData';
import type { PosthogService } from './PosthogService';
import type { OrderPublic } from '@repo/shared/UserServiceTypes';
import type { FirebaseService } from './FirebaseService';

export type AnalyticsService = {
	identifyUser: (user: UserIdentification) => Promise<void>;
	setPersonProperties: (props: Record<string, string>) => Promise<void>;
	downloadPlugin: () => void;
	initiatePurchase: (type: 'Lifetime' | 'Subscription', amount: number) => void;
	completedPurchase: (order: OrderPublic) => void;
	requestRefund: (order: OrderPublic) => void;
	deleteAccount: () => void;
	appUploadFiles: (dub_files: number) => void;
	appShowResult: () => void;
};

export function createAnalyticsService(
	posthogService: PosthogService,
	firebaseService: FirebaseService
): AnalyticsService {
	async function captureEvent(event_name: string, properties?: Properties) {
		const posthog = await posthogService.getPosthog();
		posthog?.capture(event_name, properties);
	}

	return {
		async identifyUser(user: UserIdentification) {
			const posthog = await posthogService.getPosthog();
			posthog?.identify(user.id, { name: user.username, email: user.email });
		},

		async setPersonProperties(props: Record<string, string>) {
			const posthog = await posthogService.getPosthog();
			posthog?.setPersonProperties(props);
		},

		downloadPlugin() {
			captureEvent('download-plugin');
			firebaseService.logEvent('download-plugin');
		},

		initiatePurchase(type, amount) {
			captureEvent('initiate-purchase', { type, amount });
			firebaseService.logEvent('begin_checkout', {
				currency: 'EUR',
				value: amount,
				items: [{ item_id: type, item_name: type }]
			});
		},

		completedPurchase(order) {
			const type = (() => {
				switch (order.type) {
					case 'payment':
						return 'Lifetime';
					case 'subscription':
						return 'Subscription';
				}
				throw new Error('unexpected order type');
			})();
			captureEvent('completed-purchase', { type, order_id: order.id });
			firebaseService.logEvent('purchase');
		},

		requestRefund(order) {
			captureEvent('request-refund', { order_id: order.id });
			firebaseService.logEvent('refund');
		},

		deleteAccount() {
			captureEvent('delete-account');
			firebaseService.logEvent('delete-account');
		},

		appUploadFiles(dub_files) {
			captureEvent('app-upload-files', { dub_files });
			firebaseService.logEvent('app-upload-files', { dub_files });
		},

		appShowResult() {
			captureEvent('app-show-results');
			firebaseService.logEvent('app-show-results');
		}
	};
}
